import {
  Box,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Route } from "~services/router/Route";
import { ConfirmDeleteDialog } from "~ui/molecules/ConfirmDeleteDialog/ConfirmDeleteDialog";
import { formatEventTimes } from "~utils/date";

const tEventName = "Event";
const tBeginEnd = "Event Time";
const tRsvpSchedule = "Upcoming Schedule";
const tNothingOnYourSchedule = "Nothing on your schedule, yet!";
const tRemove = "Remove";
const tEventTimesSelected = (count: number) => `${count} event time${count === 1 ? "" : "s"} selected`;

const ROW_HEIGHT = 35;
const DEFAULT_ROWS_PER_PAGE = 5;

const ROWS_PER_PAGE_BY_SIZE = {
  large: 8,
  medium: 4,
  small: 1,
};

type WidgetSize = "small" | "medium" | "large";

const getEmptyRows = (rows: unknown[], page: number, rowsPerPage: number) =>
  rows.length === 0 ? rowsPerPage : rowsPerPage - Math.min(rowsPerPage, Math.abs(rows.length - page * rowsPerPage));

const getPaginatedRows = (rows: unknown[], page: number, rowsPerPage: number) =>
  rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

const tablePaginationProps = {
  slotProps: {
    actions: {
      firstButton: { "aria-label": "first page", size: "small" as const },
      lastButton: { "aria-label": "last page", size: "small" as const },
      nextButton: { "aria-label": "next page", size: "small" as const },
      previousButton: { "aria-label": "previous page", size: "small" as const },
    },
  },
};

export interface Rsvp {
  rsvpId: string;
  beginMeetingTime: Date;
  endMeetingTime: Date;
  rsvpUserId: string;
  eventId: string;
  eventName: string;
  bubbleId?: string;
  bubbleName?: string;
}

export interface RsvpTableProps {
  rsvps: Rsvp[];
  onClickRow: (rsvpUserId: string, eventId: string) => void;
  /**
   * If true, the component will not display its own header
   * @default false
   */
  hideHeader?: boolean;
  /**
   * The widget size determines how many rows to show per page
   * small: 1, medium: 4, large: 8
   * @default undefined (will use 5 rows)
   */
  widgetSize?: WidgetSize;
  /**
   * Callback when Remove button is clicked with selected RSVP IDs
   */
  onRemove?: (rsvpIds: string[]) => void;
}

export const RsvpTable: FC<RsvpTableProps> = ({ rsvps, onClickRow, hideHeader = false, widgetSize, onRemove }) => {
  const [page, setPage] = useState(0);
  const [emptyRows, setEmptyRows] = useState(0);
  const [paginatedRows, setPaginatedRows] = useState<Rsvp[]>([]);
  const [selectedRsvps, setSelectedRsvps] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const rowsPerPage = widgetSize ? ROWS_PER_PAGE_BY_SIZE[widgetSize] : DEFAULT_ROWS_PER_PAGE;

  useEffect(() => {
    const updatedEmptyRows = getEmptyRows(rsvps, page, rowsPerPage);
    setEmptyRows(updatedEmptyRows);
    setPaginatedRows(getPaginatedRows(rsvps, page, rowsPerPage) as Rsvp[]);
  }, [rsvps, page, rowsPerPage]);

  const hasUpcomingRsvps = rsvps.length > 0;
  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = new Set(rsvps.map((rsvp) => rsvp.rsvpId));
      setSelectedRsvps(allIds);
    } else {
      setSelectedRsvps(new Set());
    }
  };

  const handleSelectOne = (rsvpId: string) => {
    const newSelected = new Set(selectedRsvps);
    if (newSelected.has(rsvpId)) {
      newSelected.delete(rsvpId);
    } else {
      newSelected.add(rsvpId);
    }
    setSelectedRsvps(newSelected);
  };

  const isSelected = (rsvpId: string) => selectedRsvps.has(rsvpId);

  const isAllSelected = rsvps.length > 0 && rsvps.every((rsvp) => selectedRsvps.has(rsvp.rsvpId));

  const isIndeterminate = selectedRsvps.size > 0 && selectedRsvps.size < rsvps.length;

  const handleRemoveClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (onRemove) {
      onRemove(Array.from(selectedRsvps));
      setSelectedRsvps(new Set());
    }
    setDeleteDialogOpen(false);
  };

  const selectedRsvpDetails = rsvps
    .filter((rsvp) => selectedRsvps.has(rsvp.rsvpId))
    .map((rsvp) => `${rsvp.eventName} (${formatEventTimes(rsvp.beginMeetingTime, rsvp.endMeetingTime)})`);

  const HEADERS = [tEventName, tBeginEnd];

  return (
    <>
      {!hideHeader && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            {tRsvpSchedule}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          minHeight: "32px",
          px: hideHeader ? 2 : 0,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectedRsvps.size > 0 ? tEventTimesSelected(selectedRsvps.size) : " "}
        </Typography>
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={selectedRsvps.size === 0}
          onClick={handleRemoveClick}
        >
          {tRemove}
        </Button>
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemType={selectedRsvps.size === 1 ? "RSVP" : "RSVPs"}
        itemName={selectedRsvpDetails}
        itemDescription={`You will be removed from ${selectedRsvps.size === 1 ? "this event time" : "these event times"}.`}
      />

      {hasUpcomingRsvps ? (
        <Box sx={{ maxWidth: "100%", minWidth: 0, overflow: "hidden", width: "100%" }}>
          <TableContainer
            sx={{
              "& .MuiTable-root": { border: "none" },
              "& .MuiTableCell-root": { borderColor: "transparent", padding: "6px 18px" },
              "& .MuiTableRow-root": { height: ROW_HEIGHT },
              border: "none",
              height: "100%",
              maxWidth: "100%",
              overflowX: "auto",
              width: "100%",
            }}
          >
            <Table sx={{ height: "100%", tableLayout: "fixed", width: "100%" }} size="small">
              <colgroup>
                <col style={{ width: "48px" }} />
                <col style={{ width: "50%" }} />
                <col style={{ width: "calc(50% - 48px)" }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      inputProps={{ "aria-label": "select all rsvps" }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                    <Typography sx={{ fontWeight: "bold" }} variant="overline">
                      {HEADERS[0]}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    <Typography sx={{ fontWeight: "bold" }} variant="overline">
                      {HEADERS[1]}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ height: "100%" }}>
                {paginatedRows.map(
                  ({ beginMeetingTime, endMeetingTime, rsvpUserId, eventId, eventName, rsvpId }, index) => {
                    const isItemSelected = isSelected(rsvpId);
                    return (
                      <TableRow
                        data-testid="rsvp-row"
                        key={index}
                        selected={isItemSelected}
                        sx={{
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                          cursor: "pointer",
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onChange={() => handleSelectOne(rsvpId)}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{ "aria-label": `select ${eventName}` }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                          <Link
                            to={Route.eventView({ eventId })}
                            onClick={() => onClickRow(rsvpUserId, eventId)}
                            style={{ color: "inherit", textDecoration: "none" }}
                          >
                            <Typography noWrap variant="body2">
                              {eventName}
                            </Typography>
                          </Link>
                        </TableCell>
                        <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                          <Link
                            to={Route.eventView({ eventId })}
                            onClick={() => onClickRow(rsvpUserId, eventId)}
                            style={{ color: "inherit", textDecoration: "none" }}
                          >
                            <Typography variant="body2">
                              {formatEventTimes(beginMeetingTime, endMeetingTime, {
                                omitDateWhenCurrentYear: true,
                              })}
                            </Typography>
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  },
                )}
                {emptyRows > 0 && (
                  <TableRow style={{ height: (ROW_HEIGHT * emptyRows) / 2 }}>
                    <TableCell colSpan={3} />
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    colSpan={Number.MAX_SAFE_INTEGER}
                    count={rsvps.length}
                    onPageChange={handleChangePage}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[]}
                    sx={{ p: "4px 0" }}
                    {...tablePaginationProps}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            height: "100px",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Typography color="text.secondary" data-testid="no-rsvps">
            {tNothingOnYourSchedule}
          </Typography>
        </Box>
      )}
    </>
  );
};
